import { Field } from "formik";
import { useEffect } from "react";
import {
  SUPPLIER_FUEL_TYPE,
  elecMeterTypeOptions,
  gasMeterTypeOptions,
  normaliseFuelType,
  paymentTypeOptions,
} from "../../constants/tariffInfo";
import { useTariff } from "../../context/TariffContext";
import EN from "../../locales/en";
import RippleLoader from "../RippleLoader";
import Select from "../Select";
import ErrorIcon from "../icons/ErrorIcon";

const EditFuelTariffFields = ({
  formik,
  fuelType,
  filterTariffs,
  filterSuppliers,
}) => {
  const {
    isTariffDataLoading,
    isSupplierDataLoading,
    setSelectedSuppliers,
    setSelectedMeterTypes,
    setSelectedPaymentMethods,
  } = useTariff();
  const index = fuelType === "electricity" ? 0 : 1;
  const currentTariff = formik.values.tariffs?.[index];
  const currentTariffErrors = formik.errors.tariffs?.[index];

  const isAllInfoForSupplierFilled =
    currentTariff?.meterType && currentTariff?.paymentMethod;

  const isAllInfoForTariffFilled =
    isAllInfoForSupplierFilled && currentTariff?.supplierName;

  const meterTypeOptions =
    normaliseFuelType(fuelType) === SUPPLIER_FUEL_TYPE.GAS
      ? gasMeterTypeOptions
      : elecMeterTypeOptions;

  useEffect(() => {
    if (isTariffDataLoading || isSupplierDataLoading) return;
    const fuelSupplierOptions = filterSuppliers(
      fuelType,
      currentTariff?.meterType,
      currentTariff?.paymentMethod
    );
    const fuelTariffOptions = filterTariffs(
      fuelType,
      currentTariff?.meterType,
      currentTariff?.paymentMethod,
      currentTariff?.supplierName
    );
    if (
      !fuelSupplierOptions
        .map((supplier) => supplier.value)
        .includes(currentTariff?.supplierName)
    ) {
      formik.setFieldValue(`tariffs.${index}.supplierName`, "");
    }
    if (
      !fuelTariffOptions
        .map((tariff) => tariff.value)
        .includes(currentTariff?.tariffName)
    ) {
      formik.setFieldValue(`tariffs.${index}.tariffName`, "");
    }
  }, [formik.values, isTariffDataLoading, isSupplierDataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border-t border-[#afb6b9] pt-6">
      <h3 className="text-[22px] font-bold">Tell us about your {fuelType}</h3>
      <div className="mt-6 space-y-6">
        <Field
          as={Select}
          id={`${fuelType}-meter-select`}
          labelText={EN.edit_tariff_form.meters[fuelType].label}
          options={meterTypeOptions}
          keyName="label"
          value={currentTariff?.meterType || ""}
          onChange={(e) => {
            formik.setFieldValue(`tariffs.${index}.meterType`, e.target.value);
            setSelectedMeterTypes({
              [fuelType]: e.target.value,
            });
          }}
          hasErrors={currentTariffErrors?.meterType}
          disabled={formik.isSubmitting}
        />

        <Field
          as={Select}
          id={`${fuelType}-payment-type`}
          labelText={EN.edit_tariff_form.payment[fuelType].title}
          options={paymentTypeOptions}
          value={currentTariff?.paymentMethod || ""}
          onChange={(e) => {
            formik.setFieldValue(
              `tariffs.${index}.paymentMethod`,
              e.target.value
            );
            setSelectedPaymentMethods({
              [fuelType]: e.target.value,
            });
          }}
          hasErrors={currentTariffErrors?.paymentMethod}
        />

        {isAllInfoForSupplierFilled && (
          <>
            {isSupplierDataLoading ? (
              <div className="mt-4">
                <RippleLoader />
              </div>
            ) : (
              <Field
                as={Select}
                id={`${fuelType}-supplier`}
                labelText={EN.edit_tariff_form.supplier[fuelType].title}
                options={filterSuppliers(
                  fuelType,
                  currentTariff?.meterType,
                  currentTariff?.paymentMethod
                )}
                value={currentTariff?.supplierName || ""}
                onChange={(e) => {
                  formik.setFieldValue(
                    `tariffs.${index}.supplierName`,
                    e.target.value
                  );
                  setSelectedSuppliers({
                    [fuelType]: e.target.value,
                  });
                }}
                hasErrors={currentTariffErrors?.supplierName}
                disabled={formik.isSubmitting}
              />
            )}
          </>
        )}
        {isAllInfoForTariffFilled && (
          <>
            {isTariffDataLoading ? (
              <div className="mt-4">
                <RippleLoader />
              </div>
            ) : (
              <div className="space-y-2">
                <Field
                  as={Select}
                  id={`${fuelType}-tariff`}
                  labelText={EN.edit_tariff_form.tariff[fuelType].title}
                  options={filterTariffs(
                    fuelType,
                    currentTariff?.meterType,
                    currentTariff?.paymentMethod,
                    currentTariff?.supplierName
                  )}
                  keyName="id"
                  value={currentTariff?.tariffName || ""}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `tariffs.${index}.tariffName`,
                      e.target.value
                    )
                  }
                  hasErrors={currentTariffErrors?.tariffName}
                  disabled={formik.isSubmitting}
                />
                {!filterTariffs(
                  fuelType,
                  currentTariff?.meterType,
                  currentTariff?.paymentMethod,
                  currentTariff?.supplierName
                )?.length && (
                  <div className="text-red-500 flex items-center">
                    <ErrorIcon className="mr-2" />
                    No tariffs found
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditFuelTariffFields;
